import { useMemo, useState, type ReactNode } from "react"
import type { Monaco } from "@monaco-editor/react"
import type { editor } from "monaco-editor"
import cn from "@/v2-ui/utils/utils.cn"
import Resizable from "@/v2-ui/resizable"
import BaseTabs from "@/v2-ui/tabs"
import Editor from "@/v2-backoffice-client/components/editor"
import LayerAiAssistant from "@/v2-backoffice-client/layer/ai/LayerAiAssistant"
import type {
  LayerAiExternalPrompt,
  LayerAiIdentityProposal,
  LayerAiSuggestableField,
} from "@/v2-backoffice-client/layer/ai/layer.ai.types"
import {
  hasLayerAiHandoff,
  type LayerAiChatTurn,
} from "@/v2-backoffice-client/layer/ai/layer.ai.handoff"
import LayerDriftFixNotice from "@/v2-backoffice-client/layer/drift/resolve/LayerDriftFixNotice"
import { useLayerDriftFix } from "@/v2-backoffice-client/layer/drift/resolve/useLayerDriftFix"
import type { LayerDriftFixIntent } from "@/v2-backoffice-client/layer/drift/resolve/layer.drift.resolve"
import {
  getLiveDriftFindings,
  toDriftDiagnostics,
} from "@/v2-backoffice-client/layer/drift/diagnostics/layer.drift.diagnostics"
import {
  useLayerDriftFindings,
  useLayerDriftMarkers,
} from "@/v2-backoffice-client/layer/drift/diagnostics/useLayerDriftFindings"
import LayerUpdateDiagnostics from "@/v2-backoffice-client/layer/update/diagnostics/LayerUpdateDiagnostics"
import LayerUpdateInspector from "@/v2-backoffice-client/layer/update/inspector/LayerUpdateInspector"
import LayerUpdatePreview from "@/v2-backoffice-client/layer/update/preview/LayerUpdatePreview"
import type { LayerPreviewSubject } from "@/v2-backoffice-client/layer/update/preview/LayerUpdatePreviewMap"
import Button from "@/v2-backoffice-client/components/button"
import {
  getLayerEditorSchema,
  type LayerEditorField,
} from "@/v2-backoffice-client/layer/update/schema/layer.update.schema"
import { registerCatalogEditorSchemas } from "@/v2-backoffice-client/feature/configuration/feature.configuration.schema"
import { inferConfigurationLayerType } from "@/v2-backoffice-client/layer/service/layer.service.metadata"

export type LayerWorkbenchAssist = {
  /** The layer's feature code; omit in the create flow (no row yet). */
  featureCode?: string
  /** Create flow: the probed source url the assistant reasons over. */
  sourceUrl?: string
  /** Display name of the layer; prefills the assistant's fork proposal. */
  name?: string
  /** Set for studio rows: the AI resolves them from the authoring catalog. */
  environment?: string
  /** Current saved blobs — the diff base for non-Configuration suggestions. */
  blobs: Partial<Record<LayerAiSuggestableField, unknown>>
  /** Persists an accepted non-Configuration suggestion. */
  onSaveField(field: LayerAiSuggestableField, value: unknown): Promise<unknown>
  isSavingField?: boolean
  /** Create flow: applies a proposed identity into the create form. */
  onIdentity?(identity: LayerAiIdentityProposal): void
  /** Create flow: mirrors the conversation for the post-create handoff. */
  onTurns?(turns: LayerAiChatTurn[]): void
  /**
   * A host-pushed message (e.g. the create form's "suggest identity"): the
   * assistant panel opens and auto-sends it, like the diagnostics' "Fix
   * with AI" push.
   */
  externalPrompt?: LayerAiExternalPrompt | null
}

/**
 * One editable JSON blob in multi-document mode: the editor pane hosts a
 * tab per document and switching tabs swaps only the editor — the preview,
 * inspector and assistant panels stay put.
 */
export type LayerWorkbenchDocument = {
  field: LayerEditorField
  /** The saved/seeded blob, pretty-printed. */
  value: string
  /** Monaco model URI; must be unique per document being edited. */
  modelPath: string
  /** Marks the tab with a content dot. */
  hasContent?: boolean
}

/**
 * The dragged editor-pane width survives tab switches and sheet reopens;
 * without a stored value the pane starts at a third of the workbench,
 * leaving two thirds to the map.
 */
const EDITOR_WIDTH_STORAGE_KEY = "backoffice.layerWorkbench.editorWidth"

function getStoredEditorWidth() {
  const raw = localStorage.getItem(EDITOR_WIDTH_STORAGE_KEY)
  const parsed = raw ? Number.parseFloat(raw) : Number.NaN
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

export type LayerWorkbenchProps = {
  className?: string
  field: LayerEditorField
  /** Drives the per-layer-type JSON schema (empty string = generic). */
  layerTypeCode: string
  /** Monaco model URI; must be unique per document being edited. */
  modelPath: string
  /** The saved blob, pretty-printed. */
  value: string
  /**
   * Multi-document mode (create flow): every blob as a tab INSIDE the
   * editor pane. `field`/`modelPath`/`value` are ignored in this mode; the
   * documents carry them. Editors stay mounted across switches, so drafts
   * and undo stacks survive.
   */
  documents?: LayerWorkbenchDocument[]
  isSaving?: boolean
  /** Receives the parsed draft (the editor validates JSON before calling). */
  onSave(value: unknown): void
  /**
   * Observes the raw draft text as it changes (create flow mirrors every
   * tab's text into the create payload). In multi-document mode use
   * `onDocumentDraftChange` instead.
   */
  onDraftChange?(text: string): void
  /** Multi-document mode's per-tab draft observer. */
  onDocumentDraftChange?(field: LayerEditorField, text: string): void
  /**
   * Enables the live map preview and the AI assistant; only passed for the
   * Configuration blob, where they make sense.
   */
  preview?: LayerPreviewSubject
  assist?: LayerWorkbenchAssist
  /**
   * The layer's service url (from its Configuration); unlocks the
   * inspector's metadata-aware guided widgets on every tab.
   */
  serviceUrl?: string | null
  /** Auto-applies preview drafts after this debounce (create flow). */
  previewAutoApplyMs?: number
  /** The save button's label; the create flow renames it "Create layer". */
  saveLabel?: ReactNode
  /** Keeps save enabled with an unchanged document (create flow). */
  canSaveClean?: boolean
  /** Opens the assistant panel from the start (create flow's AI-first UX). */
  defaultAssistantOpen?: boolean
  /**
   * Wires the document to its layer's drift: upstream changes it still
   * references are underlined in the text and listed in the diagnostics
   * pill, and a fix handed over by the Drift tab is staged as an unsaved
   * edit. Only the sheet passes this — the create flow has no baseline to
   * drift from yet.
   */
  drift?: LayerWorkbenchDrift
}

export type LayerWorkbenchDrift = {
  featureCode: string
  /** Set when the URL is handing this document a fix to stage. */
  fix?: LayerDriftFixIntent | null
  /** Fired once the fix is staged, so the host can drop the URL params. */
  onFixApplied?(): void
}

/**
 * The layer editing workbench: one consistent surface for every JSON blob of
 * a layer, shared by the runtime sheet, Layer Studio and the create flow.
 * Laid out like the ArcGIS SDK playground: the schema-assisted editor on the
 * left, the draft rendered live on a full-height map on the right, with a
 * draggable divider between them. The cursor-following inspector and the AI
 * assistant dock in as toggleable side panels.
 */
function LayerWorkbench(props: LayerWorkbenchProps) {
  const {
    field,
    layerTypeCode,
    modelPath,
    value,
    documents,
    isSaving,
    onSave,
    onDraftChange,
    onDocumentDraftChange,
    preview,
    assist,
    serviceUrl,
    previewAutoApplyMs,
    saveLabel,
    canSaveClean,
    defaultAssistantOpen,
    drift,
    className,
  } = props

  const [activeField, setActiveField] = useState<LayerEditorField>(
    documents?.[0]?.field ?? field,
  )
  // Single-doc mode reads as a one-document list; everything downstream
  // handles both modes through this.
  const allDocuments: LayerWorkbenchDocument[] = documents ?? [
    { field, value, modelPath },
  ]
  const currentDocument =
    allDocuments.find((document) => document.field === activeField) ??
    allDocuments[0]
  const effectiveField = currentDocument.field

  // One draft per document, superseded whenever its saved/seeded value
  // changes (the editor resets its text the same way): after a save both
  // are equal anyway; in the create flow a re-seed (type/sublayer change,
  // AI-attached blob) must reach the preview and the assistant instead of
  // a stale pre-seed draft.
  const [drafts, setDrafts] = useState<
    Partial<Record<LayerEditorField, string>>
  >({})
  const [prevValues, setPrevValues] = useState<
    Partial<Record<LayerEditorField, string>>
  >({})
  const supersededFields = allDocuments.filter(
    (document) => prevValues[document.field] !== document.value,
  )
  if (supersededFields.length > 0) {
    const nextPrev = { ...prevValues }
    const nextDrafts = { ...drafts }
    for (const document of supersededFields) {
      nextPrev[document.field] = document.value
      delete nextDrafts[document.field]
    }
    setPrevValues(nextPrev)
    setDrafts(nextDrafts)
  }

  // The Configuration document feeds the preview, diagnostics and the
  // assistant no matter which tab is active.
  const configurationDocument = allDocuments.find(
    (document) => document.field === "Configuration",
  )
  const configurationDraft =
    drafts.Configuration ?? configurationDocument?.value ?? ""

  // With the map filling the right half, the inspector starts closed for
  // Configuration; on the blob-only tabs there is room for it from the start.
  const [isInspectorOpen, setIsInspectorOpen] = useState(
    () => !documents && field !== "Configuration",
  )
  // Opens pre-expanded when a fork handed its conversation over — the
  // operator lands here to continue that exchange, not to hunt for it —
  // or when the host wants the AI-first entry (create flow).
  const [isAssistantOpen, setIsAssistantOpen] = useState(
    () =>
      (defaultAssistantOpen ?? false) ||
      Boolean(assist?.featureCode && hasLayerAiHandoff(assist.featureCode)),
  )
  const [editorInstances, setEditorInstances] = useState<
    Partial<Record<LayerEditorField, editor.IStandaloneCodeEditor>>
  >({})
  // Held for setModelMarkers, which needs the namespace rather than the
  // instance. Same object for every editor on the page.
  const [monaco, setMonaco] = useState<Monaco | null>(null)
  // The diagnostics' "Fix with AI" pushes its repair prompt through here;
  // the assistant auto-sends it (see LayerAiAssistant.externalPrompt).
  const [assistantPrompt, setAssistantPrompt] =
    useState<LayerAiExternalPrompt | null>(null)
  // Bumped when the AI history replays a value, forcing the preview to
  // apply the navigated-to draft immediately (no Reload button dance).
  const [previewApplyToken, setPreviewApplyToken] = useState(0)
  // Read once per mount: Resizable manages the live width itself via the
  // inline style, we only need the starting point.
  const [storedEditorWidth] = useState(getStoredEditorWidth)

  // A fix arriving from the Drift tab lands in the document's editor as an
  // unsaved edit, so it goes through review + preview + Save like anything
  // the operator typed.
  const { result: driftFixResult, dismiss: dismissDriftFix } = useLayerDriftFix(
    {
      editor: editorInstances[effectiveField],
      intent: drift?.fix ?? null,
      onApplied: drift?.onFixApplied,
    },
  )

  // Drift the document in view still references: underlined in the text,
  // and listed in the same pill as the service checks. Narrowed against
  // the live draft so both clear as the operator types the fix, without
  // waiting for a save and a re-probe.
  const activeDraft = drafts[effectiveField] ?? currentDocument.value
  const reportedDrift = useLayerDriftFindings(
    drift?.featureCode,
    effectiveField,
  )
  // Memoized because it feeds the marker effect's dependencies: a fresh
  // array each render would re-publish the markers on every render
  // rather than when the text or the drift actually changed.
  const driftFindings = useMemo(
    () => getLiveDriftFindings(reportedDrift, activeDraft),
    [reportedDrift, activeDraft],
  )
  useLayerDriftMarkers({
    editor: editorInstances[effectiveField],
    monaco,
    findings: driftFindings,
    draft: activeDraft,
  })

  // A host-pushed assistant message opens the panel, exactly like the
  // diagnostics push does through its click handler.
  const hostPrompt = assist?.externalPrompt ?? null
  const [prevHostPromptId, setPrevHostPromptId] = useState<number | null>(null)
  if (hostPrompt && hostPrompt.id !== prevHostPromptId) {
    setPrevHostPromptId(hostPrompt.id)
    setIsAssistantOpen(true)
  }
  const effectivePrompt =
    hostPrompt && (!assistantPrompt || hostPrompt.id >= assistantPrompt.id)
      ? hostPrompt
      : assistantPrompt

  // Panel toggles live in the editor toolbar (one consistent surface for
  // every editor action); the open state shows through the pressed variant.
  const panelToggles = (
    <>
      <Button
        variant={isInspectorOpen ? "secondary" : "ghost"}
        size="sm"
        iconSrc="list"
        aria-pressed={isInspectorOpen}
        title={isInspectorOpen ? "Close the inspector" : "Open the inspector"}
        data-feature-code="UI_BackofficeLayers_Workbench_Inspector"
        onClick={() => setIsInspectorOpen((open) => !open)}
      >
        Inspector
      </Button>
      {assist && (
        <Button
          variant={isAssistantOpen ? "secondary" : "ghost"}
          size="sm"
          iconSrc="sparkles"
          aria-pressed={isAssistantOpen}
          title={
            isAssistantOpen ? "Close the AI assistant" : "Open the AI assistant"
          }
          data-feature-code="UI_BackofficeLayers_Workbench_Assistant"
          onClick={() => setIsAssistantOpen((open) => !open)}
        >
          AI assistant
        </Button>
      )}
    </>
  )

  // Every document keeps its own mounted editor (hidden when inactive):
  // Monaco models, undo stacks and unsaved drafts survive tab switches for
  // free, and the visible one is just a class flip away.
  const editorPane = (
    <>
      {documents && (
        <BaseTabs
          className="shrink-0"
          value={activeField}
          onValueChange={(next) => setActiveField(next as LayerEditorField)}
        >
          <BaseTabs.List className="w-fit">
            {documents.map((document) => (
              <BaseTabs.Trigger key={document.field} value={document.field}>
                {document.field}
                {document.hasContent && (
                  <span
                    className="ml-1 inline-block size-1.5 rounded-full bg-primary"
                    title="This tab has content that will be part of the new layer"
                  />
                )}
              </BaseTabs.Trigger>
            ))}
          </BaseTabs.List>
        </BaseTabs>
      )}

      {allDocuments.map((document) => (
        // Visibility lives on a neutral wrapper: the Editor root's own
        // `.editor { display: flex }` (plain CSS, later in the bundle)
        // would win over a `hidden` utility at equal specificity.
        <div
          key={document.field}
          className={
            document.field === effectiveField
              ? "flex min-h-0 flex-1 flex-col"
              : "hidden"
          }
        >
          <Editor
            className="min-h-0 flex-1"
            value={document.value}
            path={document.modelPath}
            beforeMount={registerCatalogEditorSchemas}
            onMount={(instance, monacoInstance) => {
              setEditorInstances((current) => ({
                ...current,
                [document.field]: instance,
              }))
              setMonaco(monacoInstance)
            }}
            isSaving={isSaving}
            saveLabel={saveLabel}
            canSaveClean={canSaveClean}
            onChange={(text) => {
              setDrafts((current) => ({ ...current, [document.field]: text }))
              onDraftChange?.(text)
              onDocumentDraftChange?.(document.field, text)
            }}
            onSave={(text) => onSave(JSON.parse(text))}
            toolbarExtras={panelToggles}
            // Findings float in the code area's corner instead of eating a
            // toolbar row. Every document gets the pill so drift is
            // reported where it applies; only Configuration carries a
            // service `url`, so only it runs the live service checks.
            overlay={
              <LayerUpdateDiagnostics
                draft={
                  document.field === "Configuration"
                    ? configurationDraft
                    : (drafts[document.field] ?? document.value)
                }
                withServiceChecks={document.field === "Configuration"}
                extra={
                  document.field === effectiveField
                    ? toDriftDiagnostics(driftFindings)
                    : undefined
                }
                onFixWithAi={
                  assist && document.field === "Configuration"
                    ? (prompt) => {
                        setIsAssistantOpen(true)
                        setAssistantPrompt({ id: Date.now(), text: prompt })
                      }
                    : undefined
                }
              />
            }
          />
        </div>
      ))}
    </>
  )

  return (
    <div
      className={cn(
        "layer-workbench flex min-h-[520px] flex-1 flex-col gap-2 pt-2",
        className,
      )}
    >
      {driftFixResult && (
        <LayerDriftFixNotice
          result={driftFixResult}
          onDismiss={dismissDriftFix}
        />
      )}

      <div className="flex min-h-0 flex-1 gap-2">
        {preview ? (
          // Split-screen: the editor pane owns its width via the drag
          // handle on its right edge; the map takes the rest.
          <Resizable
            direction="vertical"
            position="end"
            handlePlacement="after"
            minVal={300}
            showGrip
            className="flex w-1/3 min-w-[300px] max-w-[70%] shrink-0 flex-col gap-2"
            style={
              storedEditorWidth
                ? { width: `${storedEditorWidth}px` }
                : undefined
            }
            onResizeEnd={(val) =>
              localStorage.setItem(EDITOR_WIDTH_STORAGE_KEY, String(val))
            }
          >
            {editorPane}
          </Resizable>
        ) : (
          <div className="flex min-w-0 flex-1 flex-col gap-2">{editorPane}</div>
        )}

        {isInspectorOpen && (
          // The inspector owns its width through the handle on its own
          // right edge (each panel drags itself, never a neighbour).
          <Resizable
            direction="vertical"
            position="end"
            handlePlacement="after"
            minVal={220}
            showGrip
            className="flex w-[300px] min-w-[220px] max-w-[45%] shrink-0 flex-col"
          >
            <LayerUpdateInspector
              className="min-h-0 flex-1"
              editor={editorInstances[effectiveField] ?? null}
              schema={getLayerEditorSchema(effectiveField, layerTypeCode)}
              field={effectiveField}
              serviceUrl={serviceUrl}
              onClose={() => setIsInspectorOpen(false)}
            />
          </Resizable>
        )}

        {preview && (
          <LayerUpdatePreview
            className="min-w-[240px] flex-1"
            // The draft's shape wins over the stored type, so the preview
            // stays honest BEFORE a save persists the flip: a group draft
            // turned flat FeatureLayer config would otherwise build an
            // empty group and render nothing.
            subject={{
              ...preview,
              LayerTypeCode:
                inferConfigurationLayerType(configurationDraft) ??
                preview.LayerTypeCode,
            }}
            draft={configurationDraft}
            autoApplyMs={previewAutoApplyMs}
            applyToken={previewApplyToken}
          />
        )}

        {assist && isAssistantOpen && (
          // Rightmost panel: its handle sits on its left edge, facing
          // the map it takes space from.
          <Resizable
            direction="vertical"
            position="start"
            handlePlacement="before"
            minVal={280}
            showGrip
            className="flex w-[360px] min-w-[280px] max-w-[50%] shrink-0 flex-col"
          >
            <LayerAiAssistant
              className="min-h-0 flex-1"
              featureCode={assist.featureCode}
              sourceUrl={assist.sourceUrl}
              layerName={assist.name}
              environment={assist.environment}
              externalPrompt={effectivePrompt}
              blobs={assist.blobs}
              draft={configurationDraft}
              isSavingField={assist.isSavingField}
              onSaveField={assist.onSaveField}
              onIdentity={assist.onIdentity}
              onTurns={assist.onTurns}
              onHistoryNavigated={() =>
                setPreviewApplyToken((token) => token + 1)
              }
              onClose={() => setIsAssistantOpen(false)}
              onApply={(configuration) => {
                // Suggestions always land on the Configuration document,
                // whichever tab is showing.
                const target =
                  editorInstances.Configuration ??
                  editorInstances[effectiveField]
                const model = target?.getModel()
                if (!model || !target) return
                // Through executeEdits (not setValue) so Cmd+Z undoes the
                // applied suggestion like any typed edit.
                target.executeEdits("ai-assistant", [
                  { range: model.getFullModelRange(), text: configuration },
                ])
                // An applied suggestion is a finished edit, not typing in
                // progress: put it on the map at once instead of leaving
                // the operator to find the Reload button.
                setPreviewApplyToken((token) => token + 1)
              }}
            />
          </Resizable>
        )}
      </div>
    </div>
  )
}

export default LayerWorkbench
